import Content from './Content';
import { SliderProps } from './props';

import { useCustomCarousel } from '@starsoft/common/hooks';
import { PropsWithChildren, ReactNode } from 'react';
import { NoDataComponent } from '../NoDataComponent';
import LoadMore from './LoadMore';
import styles from './styles.module.scss';

/**
 * Slider Component
 * A customizable carousel/slider component that supports auto-scrolling, pagination,
 * custom headers, and various display options.
 *
 * @component
 * @param {Object} props - Component props
 * @param {number} props.size - Number of items to show per slide
 * @param {number} props.timeout - Auto-scroll interval in milliseconds
 * @param {ReactNode} props.children - Slider content/items
 * @param {Object} props.paginated - Pagination configuration and handlers
 * @param {string} props.className - Additional CSS class names
 * @param {number} props.mobileSize - Number of items to show on mobile screens
 * @param {boolean} props.hideButtons - Whether to hide navigation buttons
 * @param {number} props.slideBy - Number of items to slide by (default: 1)
 * @param {number} props.currentIndex - Current active slide index
 * @param {(index: number) => void} props.onStepChange - Callback when slide changes
 * @param {boolean} props.useAutoScroll - Whether to enable auto-scrolling
 * @param {boolean} props.absoluteButton - Whether buttons should have absolute positioning
 * @param {Object} props.customComponents - Custom rendering components
 * @param {boolean} props.showButtonsOnScroll - Show buttons only when scrolling
 * @param {boolean} props.scrollToCurrentOnEnter - Scroll to current index on component mount
 * @param {'horizontal' | 'vertical'} props.direction - Scroll direction
 * @returns {JSX.Element} The rendered slider component
 *
 * @example
 * <Slider
 *   size={4}
 *   timeout={3000}
 *   mobileSize={2}
 *   slideBy={2}
 *   currentIndex={0}
 *   onStepChange={(index) => function(`Current slide: ${index}`)}
 *   useAutoScroll={true}
 *   direction="horizontal"
 *   paginated={{
 *     loading: false,
 *     hasMore: true,
 *     onLoadMore: () => fetchMoreItems()
 *   }}
 *   customComponents={{
 *     header: ({ onDragLeft, onDragRight }) => (
 *       <CustomHeader onPrev={onDragLeft} onNext={onDragRight} />
 *     )
 *   }}
 * >
 *   <SlideItem>Item 1</SlideItem>
 *   <SlideItem>Item 2</SlideItem>
 *   <SlideItem>Item 3</SlideItem>
 * </Slider>
 */
export function Slider({
  size,
  timeout,
  children,
  paginated,
  className,
  mobileSize,
  hideButtons,
  slideBy = 1,
  currentIndex,
  onStepChange,
  useAutoScroll,
  absoluteButton,
  customComponents,
  showButtonsOnScroll,
  scrollToCurrentOnEnter,
  direction,
}: PropsWithChildren<SliderProps>) {
  const { handleDragLeft, handleDragRight, ...rest } = useCustomCarousel({
    autoScroll: useAutoScroll,
    size,
    onStepChange,
    timeout,
    currentIndex,
    scrollToCurrentOnEnter,
    slideBy,
    direction,
  });

  // Return NoDataComponent if no children are provided
  if ((children as ReactNode[])?.length === 0) {
    return (
      <div className={`${styles.container} ${className ? className : ''}`}>
        {customComponents?.header
          ? customComponents?.header({
              onDragLeft: () => handleDragLeft(),
              onDragRight: () => handleDragRight(),
            })
          : null}
        <NoDataComponent isSecondaryStyles isSmall />
      </div>
    );
  }

  // Render slider with content and optional pagination
  return (
    <div className={`${styles.container} ${className ? className : ''}`}>
      {customComponents?.header
        ? customComponents?.header({
            onDragLeft: () => handleDragLeft(),
            onDragRight: () => handleDragRight(),
          })
        : null}
      <Content
        showButtonsOnScroll={showButtonsOnScroll}
        size={size}
        containerProps={{ ...rest }}
        hideButtons={hideButtons}
        absoluteButton={absoluteButton}
        mobileSize={mobileSize}
        slideBy={slideBy}
      >
        {children}
        {paginated && !paginated?.loading && <LoadMore {...paginated} />}
      </Content>
    </div>
  );
}
